import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Policy } from '../../Models/policy.model';
import { PolicyService } from '../../Services/policy.service';
import { AuthenticationService } from '../../Services/authentication.service';

@Component({
    selector: 'app-policies',
    templateUrl: './policies.component.html',
    styleUrls: ['./policies.component.css']
})
export class PoliciesComponent implements OnInit {
    title: string = 'Company Policies';
    loading: boolean = false;
    errorMessage: string = '';
    private queryPolicySlug: string = '';

    policies: Policy[] = [];
    filteredPolicies: Policy[] = [];
    pagedPolicies: Policy[] = [];

    searchText: string = '';
    pageSizeOptions: number[] = [ 20, 50];
    pageSize: number = 20;
    currentPage: number = 1;
    totalPages: number = 1;

    constructor(
        private policyService: PolicyService,
        private authenticationService: AuthenticationService,
        private router: Router,
        private route: ActivatedRoute
    ) { }

    ngOnInit() {
        this.loadPolicies();
        this.subscribeToQueryParams();
    }

    loadPolicies() {
        this.loading = true;
        this.errorMessage = '';

        this.policyService.getPolicies().subscribe(
            data => {
                this.policies = data || [];
                this.applyFilterAndPaging();
                this.updateTitleFromQueryParam();
                this.loading = false;
            },
            error => {
                this.loading = false;
                this.handleHttpError(error);
            }
        );
    }

    
    onSearchChange() {
        this.currentPage = 1;
        this.applyFilterAndPaging();
    }

    onPageSizeChange() {
        this.currentPage = 1;
        this.applyFilterAndPaging();
    }

    goToPage(page: number) {
        if (page < 1 || page > this.totalPages) {
            return;
        }
        this.currentPage = page;
        this.setPagedRecords();
    }

    viewPolicy(policy: Policy) {
        const policyNameForUrl = this.toUrlSlug(policy.policyName);
        this.router.navigate(['/policies/view', policy.policyID], {
            queryParams: { name: policyNameForUrl }
        });
    }

    private applyFilterAndPaging() {
        const search = (this.searchText || '').toLowerCase().trim();

        this.filteredPolicies = !search
            ? this.policies.slice(0)
            : this.policies.filter(policy =>
                (policy.policyName || '').toLowerCase().indexOf(search) > -1 ||
                (policy.description || '').toLowerCase().indexOf(search) > -1
            );

        this.totalPages = Math.max(1, Math.ceil(this.filteredPolicies.length / this.pageSize));

        if (this.currentPage > this.totalPages) {
            this.currentPage = this.totalPages;
        }

        this.setPagedRecords();
    }

    private setPagedRecords() {
        const start = (this.currentPage - 1) * this.pageSize;
        const end = start + this.pageSize;
        this.pagedPolicies = this.filteredPolicies.slice(start, end);
    }

    private handleHttpError(error: any) {
        const statusCode = error && error.status;

        if (statusCode === 401) {
            this.errorMessage = 'Session expired. Please login again.';
            this.authenticationService.logout();
            return;
        }
        if (statusCode === 403) {
            this.errorMessage = 'You are not authorized to view this policy.';
            return;
        }
        if (statusCode === 404) {
            this.errorMessage = 'Policy not found.';
            return;
        }

        this.errorMessage = this.extractErrorMessage(error) || 'An unexpected error occurred.';
    }

    private extractErrorMessage(error: any): string {
        if (!error) {
            return '';
        }

        if (error._body) {
            try {
                const parsedBody = JSON.parse(error._body);
                if (parsedBody && parsedBody.message) {
                    return parsedBody.message;
                }
                if (parsedBody && parsedBody.errorMessage) {
                    return parsedBody.errorMessage;
                }
            } catch (e) {
                if (typeof error._body === 'string' && error._body.trim()) {
                    return error._body;
                }
            }
        }

        if (error.message) {
            return error.message;
        }

        return '';
    }

    private subscribeToQueryParams() {
        this.route.queryParams.subscribe(params => {
            const name = params['name'];
            if (name) {
                this.queryPolicySlug = name;
                this.updateTitleFromQueryParam();
                return;
            }

            this.title = 'Company Policies';
        });
    }

    private updateTitleFromQueryParam() {
        if (!this.queryPolicySlug) {
            return;
        }

        const matchedPolicy = this.policies.find(policy =>
            this.toUrlSlug(policy.policyName) === this.queryPolicySlug
        );

        if (matchedPolicy && matchedPolicy.policyName) {
            this.title = matchedPolicy.policyName;
            return;
        }

        this.title = this.queryPolicySlug.replace(/[_-]+/g, ' ');
    }

    private toUrlSlug(value: string): string {
        return (value || '')
            .trim()
            .replace(/[^a-zA-Z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '');
    }
}



// WEBPACK FOOTER //
// ./src/app/Components/Policies/policies.component.ts